<template>
  <div class="gc-wo-bulk-update-step-configure">
    <div class="configure-layout">
      <!-- ── Main column ──────────────────────────────────────────────────── -->
      <div class="configure-main">
        <!-- Metric cards row -->
        <div class="cfg-metrics">
          <div class="cfg-metric-card">
            <span class="cfg-metric-label">Work orders matched</span>
            <span class="cfg-metric-value">{{ localWoList.length }}</span>
          </div>
          <div class="cfg-metric-card">
            <span class="cfg-metric-label">Materials matched</span>
            <span class="cfg-metric-value">{{
              uniqueMatchedMaterialsCount
            }}</span>
          </div>
          <div class="cfg-metric-card">
            <span class="cfg-metric-label">Selected</span>
            <span
              :class="[
                'cfg-metric-value',
                includedCount === 0 ? 'cfg-val-zero' : 'cfg-val-selected',
              ]"
              >{{ includedCount }}</span
            >
          </div>
          <div class="cfg-metric-card">
            <span class="cfg-metric-label">Excluded</span>
            <span
              :class="[
                'cfg-metric-value',
                excludedCount > 0 ? 'cfg-val-excluded' : '',
              ]"
              >{{ excludedCount }}</span
            >
          </div>
        </div>

        <!-- Zero-WO guard -->
        <div
          v-if="includedCount === 0 && localWoList.length > 0"
          class="cfg-zero-wo"
        >
          All work orders have been excluded. Include at least one work order to
          continue.
        </div>

        <!-- WO results section -->
        <div class="cfg-wo-section">
          <div class="cfg-wo-section-hdr">
            <span class="cfg-wo-section-title"
              >Work order results — select scope</span
            >
            <div v-if="!isReadOnly" class="cfg-wo-section-actions">
              <button class="cfg-scope-btn" @click="selectAll">
                Select all
              </button>
              <button class="cfg-scope-btn" @click="clearAll">Clear all</button>
            </div>
          </div>

          <!-- Empty state -->
          <div v-if="localWoList.length === 0" class="cfg-wo-empty">
            No work orders matched the search criteria.
          </div>

          <DxDataGrid
            v-else
            ref="woGrid"
            :data-source="localWoList"
            :show-borders="false"
            :column-auto-width="false"
            :word-wrap-enabled="false"
            :hover-state-enabled="true"
            :row-alternation-enabled="false"
            :show-column-lines="false"
            :show-row-lines="true"
            key-expr="Wono"
            class="cfg-wo-grid"
            :column-chooser="{ enabled: false }"
            :selected-row-keys="selectedRowKeys"
            @selection-changed="onSelectionChanged"
            @row-prepared="onRowPrepared"
          >
            <DxSelection mode="multiple" show-check-boxes-mode="always" />
            <DxSearchPanel :visible="false" />

            <DxColumn
              data-field="Wono"
              caption="Work Order #"
              :width="getMinWidthForGrid('WorkOrder').width"
              cell-template="wono-cell"
              css-class="wo-id-column"
            />
            <DxColumn
              data-field="ProductName"
              caption="Product"
              :minWidth="getMinWidthForGrid('ProductDescription').minWidth"
            />

            <DxColumn
              caption="Matched"
              cell-template="matched-cell"
              :width="300"
              :allow-sorting="false"
            />

            <!-- Matched pills -->
            <template #matched-cell="{ data }">
              <div class="matched-pills">
                <span
                  v-for="pill in getMatchedPills(data.data)"
                  :key="pill.key"
                  class="matched-pill"
                  >{{ pill.label }}</span
                >
              </div>
            </template>

            <DxColumn
              caption="Status"
              cell-template="status-cell"
              :width="120"
              :allow-sorting="false"
            />

            <DxColumn
              data-field="DateChanged"
              caption="Last Updated"
              data-type="date"
              :format="'dd MMM yyyy'"
              :minWidth="getMinWidthForGrid('Date').minWidth"
              :width="getMinWidthForGrid('Date').width"
            />

            <DxPaging :page-size="20" />
            <DxPager
              :show-page-size-selector="true"
              :allowed-page-sizes="[10, 20, 50]"
              :show-info="true"
            />

            <!-- WONO cell — integer, no prefix, id-styled -->
            <template #wono-cell="{ data }">
              <span class="wo-id-cell">{{ data.data.Wono }}</span>
            </template>

            <!-- Status badge -->
            <template #status-cell="{ data }">
              <GcStatusChip
                :status="woStatusLabel(data.data.Status)"
                :data="data.data"
              />
            </template>
          </DxDataGrid>

          <div v-if="localWoList.length > 0" class="cfg-wo-footer">
            Showing {{ localWoList.length }}
            {{ localWoList.length === 1 ? "work order" : "work orders" }}
            matched
          </div>
        </div>

        <!-- Material update operation panel -->
        <GcWoBulkUpdateMaterialConfig
          ref="materialConfig"
          :value="localMaterialUpdateConfig"
          :matched-wo-list="localWoList"
          :is-read-only="isReadOnly"
          @config-change="onMaterialConfigChange"
          @validation-change="onMaterialValidationChange"
        />
      </div>
      <!-- /configure-main -->

      <!-- ── Sidebar ──────────────────────────────────────────────────────── -->
      <div class="configure-sidebar">
        <!-- Active filters -->
        <div class="cfg-sidebar-card">
          <div class="cfg-sidebar-card-hdr">Active filters</div>
          <div class="cfg-sidebar-card-body">
            <template v-if="activeFilterGroups.length > 0">
              <div
                v-for="group in activeFilterGroups"
                :key="group.label"
                class="cfg-filter-group"
              >
                <span class="cfg-filter-group-label">{{ group.label }}</span>
                <div class="cfg-filter-chips">
                  <span
                    v-for="(chip, ci) in group.chips"
                    :key="ci"
                    class="cfg-filter-chip"
                    >{{ chip }}</span
                  >
                </div>
              </div>
            </template>
            <span v-else class="cfg-filter-empty">No filters applied</span>
          </div>
        </div>

        <!-- Configure tip -->
        <div class="cfg-sidebar-card">
          <div class="cfg-sidebar-card-hdr">Configure tip</div>
          <div class="cfg-sidebar-card-body cfg-tip-body">
            The Material Update panel corresponds to the search criteria defined
            in Step 1. Select one operation — it must be valid before proceeding
            to Preview. All quantities are unit quantities, not extended work
            order quantities.
          </div>
        </div>
      </div>
      <!-- /configure-sidebar -->
    </div>
  </div>
</template>

<script>
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxSearchPanel,
  DxSelection,
} from "devextreme-vue/data-grid";
import GcWoBulkUpdateMaterialConfig from "@/components/production/work-order-bulk-update/gc-wo-bulk-update-material-config";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";

export default {
  name: "GcWoBulkUpdateStepConfigure",
  components: {
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxSearchPanel,
    DxSelection,
    GcWoBulkUpdateMaterialConfig,
  },
  props: {
    matchedWoList: { type: Array, default: () => [] },
    excludedWoIds: { type: Array, default: () => [] },
    savedCriteria: { type: Object, default: null },
    materialUpdateConfig: { type: Object, default: null },
    isReadOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      localWoList: [],
      localExcludedIds: [],
      localMaterialUpdateConfig: null,
    };
  },
  computed: {
    includedCount() {
      return this.localWoList.filter(
        (w) => !this.localExcludedIds.includes(w.Wono),
      ).length;
    },
    excludedCount() {
      return this.localWoList.length - this.includedCount;
    },
    selectedRowKeys() {
      return this.localWoList
        .map((w) => w.Wono)
        .filter((id) => !this.localExcludedIds.includes(id));
    },
    uniqueMatchedMaterialsCount() {
      const codes = new Set();
      for (const wo of this.localWoList) {
        const mats = wo.MatchedMaterials || [];
        for (const m of mats) {
          if (m.Code) codes.add(m.Code);
        }
      }
      return codes.size;
    },
    activeFilterGroups() {
      const sc = this.savedCriteria || {};
      const groups = [];

      // Material criteria — extract codes from CodeOnly and CodeAndQty blocks
      const matCriteria = sc.MaterialCriteria || sc.materialCriteria || [];
      const materialCodes = new Set();
      for (const block of matCriteria) {
        const codes = block.MaterialCodes || block.materialCodes || [];
        for (const code of codes) {
          materialCodes.add(code);
        }
      }
      if (materialCodes.size > 0) {
        groups.push({ label: "Material codes", chips: [...materialCodes] });
      }

      // Material qty range blocks
      const qtyBlocks = matCriteria.filter(function (b) {
        return b.SearchType === "QtyRangeOnly" || b.SearchType === "CodeAndQty";
      });
      if (qtyBlocks.length > 0) {
        const chips = qtyBlocks.map(function (b) {
          var parts = [];
          if (b.MinQty != null) parts.push("Min " + b.MinQty);
          if (b.MaxQty != null) parts.push("Max " + b.MaxQty);
          if (b.UnitOfMeasure) parts.push(b.UnitOfMeasure);
          return parts.length > 0 ? parts.join(" – ") : "Qty range";
        });
        groups.push({ label: "Qty range", chips: chips });
      }

      // Material attribute criteria
      var hasMatAttr = matCriteria.some(function (b) {
        return b.SearchType === "MaterialAttributes";
      });
      if (hasMatAttr) {
        groups.push({ label: "Material attributes", chips: ["Applied"] });
      }

      // WO scope filters
      const sf = sc.ScopeFilters || sc.scopeFilters || {};

      var woStatuses = sf.WoStatuses || sf.woStatuses || [];
      if (woStatuses.length > 0) {
        groups.push({
          label: "Work Order Status",
          chips: woStatuses.map(this.woStatusLabel),
        });
      }
      if (sf.WoTypeCode || sf.woTypeCode) {
        groups.push({
          label: "Work Order Type",
          chips: [sf.WoTypeCode || sf.woTypeCode],
        });
      }
      if (sf.BranchCode || sf.branchCode) {
        groups.push({
          label: "Branch",
          chips: [sf.BranchCode || sf.branchCode],
        });
      }
      if (sf.DivisionCode || sf.divisionCode) {
        groups.push({
          label: "Division",
          chips: [sf.DivisionCode || sf.divisionCode],
        });
      }
      if (sf.ProductCode || sf.productCode) {
        groups.push({
          label: "Product",
          chips: [sf.ProductCode || sf.productCode],
        });
      }
      if (sf.ScheduledDateFrom || sf.scheduledDateFrom) {
        groups.push({
          label: "Scheduled from",
          chips: [sf.ScheduledDateFrom || sf.scheduledDateFrom],
        });
      }
      if (sf.ScheduledDateTo || sf.scheduledDateTo) {
        groups.push({
          label: "Scheduled to",
          chips: [sf.ScheduledDateTo || sf.scheduledDateTo],
        });
      }

      return groups;
    },
  },
  watch: {
    matchedWoList(newVal) {
      this.localWoList = newVal ? [...newVal] : [];
    },
    excludedWoIds(newVal) {
      if (JSON.stringify(newVal) !== JSON.stringify(this.localExcludedIds)) {
        this.localExcludedIds = newVal ? [...newVal] : [];
        this.repaintGrid();
      }
    },
    localExcludedIds() {
      this.repaintGrid();
    },
    materialUpdateConfig(newVal) {
      if (
        JSON.stringify(newVal) !==
        JSON.stringify(this.localMaterialUpdateConfig)
      ) {
        this.localMaterialUpdateConfig = newVal
          ? JSON.parse(JSON.stringify(newVal))
          : null;
      }
    },
  },
  created() {
    this.localWoList = this.matchedWoList ? [...this.matchedWoList] : [];
    this.localExcludedIds = this.excludedWoIds ? [...this.excludedWoIds] : [];
    this.localMaterialUpdateConfig = this.materialUpdateConfig
      ? JSON.parse(JSON.stringify(this.materialUpdateConfig))
      : null;
  },
  methods: {
    getMinWidthForGrid(column) {
      return ColumnWidth[column];
    },
    repaintGrid() {
      this.$nextTick(() => {
        if (this.$refs.woGrid && this.$refs.woGrid.instance) {
          this.$refs.woGrid.instance.repaint();
        }
      });
    },
    getMatchedPills(wo) {
      const pills = [];
      const mats = wo.MatchedMaterials || [];
      for (const m of mats) {
        if (m.Code) {
          const qty = m.Qty != null ? m.Qty : null;
          pills.push({
            key: m.Code,
            label: qty != null ? m.Code + " × " + qty : m.Code,
          });
        }
      }
      return pills;
    },
    woStatusLabel(status) {
      const s = (status || "").trim();
      const map = {
        D: "Draft",
        P: "Planning",
        A: "Awaiting Production",
        I: "In Production",
        R: "Review",
        C: "Complete",
        X: "Cancelled",
      };
      return map[s] || s;
    },
    woStatusClass(status) {
      const s = (status || "").trim();
      const map = {
        D: "wo-status-draft",
        P: "wo-status-planning",
        A: "wo-status-awaiting",
        I: "wo-status-in-production",
        R: "wo-status-review",
        C: "wo-status-complete",
        X: "wo-status-cancelled",
      };
      return map[s] || "";
    },
    onSelectionChanged(e) {
      const allKeys = this.localWoList.map((w) => w.Wono);
      this.localExcludedIds = allKeys.filter(
        (k) => !e.selectedRowKeys.includes(k),
      );
      this.$emit("excluded-ids-change", [...this.localExcludedIds]);
    },
    onRowPrepared(e) {
      if (e.rowType === "data" && this.localExcludedIds.includes(e.data.Wono)) {
        e.rowElement.classList.add("wo-row-excluded");
      } else if (e.rowType === "data") {
        e.rowElement.classList.remove("wo-row-excluded");
      }
    },
    selectAll() {
      this.localExcludedIds = [];
      this.$emit("excluded-ids-change", []);
    },
    clearAll() {
      this.localExcludedIds = this.localWoList.map((w) => w.Wono);
      this.$emit("excluded-ids-change", [...this.localExcludedIds]);
    },
    onMaterialConfigChange(config) {
      this.localMaterialUpdateConfig = config;
      this.$emit("material-config-update", config);
    },
    onMaterialValidationChange(e) {
      this.$emit("validation-change", e);
    },
    validate() {
      if (this.$refs.materialConfig) {
        return this.$refs.materialConfig.validate();
      }
      return false;
    },
  },
};
</script>

<style lang="scss">
.gc-wo-bulk-update-step-configure {
  .wo-id-column {
    text-align: left !important;
  }
  // ── Two-column layout ──────────────────────────────────────────────────────
  .configure-layout {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }

  .configure-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .configure-sidebar {
    width: 248px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  // ── Sidebar cards ──────────────────────────────────────────────────────────
  .cfg-sidebar-card {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    overflow: hidden;
  }

  .cfg-sidebar-card-hdr {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #eaecf0;
    font-size: 14px;
    font-weight: 700;
    color: #101828;
  }

  .cfg-sidebar-card-body {
    padding: 16px;
    // padding-top: unset;
  }

  // ── Active filter groups ───────────────────────────────────────────────────
  .cfg-filter-group {
    margin-bottom: 0;
  }

  .cfg-filter-group-label {
    display: block;
    font-size: 11px;
    font-weight: 500;
    color: #98a2b3;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-top: 14px;
    margin-bottom: 6px;
  }

  .cfg-filter-group:first-child .cfg-filter-group-label {
    margin-top: 0;
  }

  .cfg-filter-chips {
    display: flex;
    flex-wrap: wrap;
  }

  .cfg-filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #fff;
    border: 1px solid #d0d5dd;
    border-radius: 6px;
    padding: 2px 4px 2px 9px;
    font-size: 13px;
    font-weight: 500;
    color: #344054;
    margin: 3px 3px 0 0;
  }

  .cfg-filter-empty {
    font-size: 13px;
    color: #98a2b3;
  }

  // ── Configure tip ──────────────────────────────────────────────────────────
  .cfg-tip-body {
    padding-top: 16px;
    font-size: 12px;
    color: #475467;
    line-height: 1.6;
  }

  // ── Metric cards ───────────────────────────────────────────────────────────
  .cfg-metrics {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }

  .cfg-metric-card {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 18px;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
  }

  .cfg-metric-label {
    font-size: 12px;
    font-weight: 500;
    color: #6c757d;
  }

  .cfg-metric-value {
    font-size: 28px;
    font-weight: 700;
    color: #101828;
    line-height: 1;

    &.cfg-val-selected {
      color: #03a9f4;
    }

    &.cfg-val-excluded {
      color: #dc3545;
    }

    &.cfg-val-zero {
      color: #dc3545;
    }
  }

  // ── Zero-WO guard ──────────────────────────────────────────────────────────
  .cfg-zero-wo {
    padding: 10px 14px;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 6px;
    font-size: 13px;
    color: #856404;
  }

  // ── WO results section ─────────────────────────────────────────────────────
  .cfg-wo-section {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    overflow: hidden;
  }

  .cfg-wo-section-hdr {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid #dee2e6;
  }

  .cfg-wo-section-title {
    font-size: 14px;
    font-weight: 600;
    color: #101828;
  }

  .cfg-wo-section-actions {
    display: flex;
    gap: 8px;
  }

  .cfg-scope-btn {
    display: inline-flex;
    align-items: center;
    height: 32px;
    padding: 0 14px;
    background: #fff;
    border: 1px solid #d0d5dd;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #344054;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s, border-color 0.15s;

    &:hover {
      background: #f9fafb;
      border-color: #adb5bd;
    }

    &:active {
      background: #f2f4f7;
    }
  }

  // ── Empty state ────────────────────────────────────────────────────────────
  .cfg-wo-empty {
    padding: 40px 32px;
    text-align: center;
    font-size: 13px;
    color: #98a2b3;
  }

  // ── Excluded row ───────────────────────────────────────────────────────────
  .wo-row-excluded {
    opacity: 0.4;

    td {
      text-decoration: line-through;
      text-decoration-color: #98a2b3;
    }
  }

  // ── WONO id cell ───────────────────────────────────────────────────────────
  .wo-id-cell {
    font-weight: 600;
    color: #03a9f4;
  }

  // ── Matched materials pills ────────────────────────────────────────────────
  .matched-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 2px 0;
  }

  .matched-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    background: #e0f2fe;
    color: #0369a1;
    font-size: 12px;
    font-weight: 500;
    border-radius: 9999px;
    white-space: nowrap;
  }

  // ── WO status badges ───────────────────────────────────────────────────────
  .wo-status-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;

    &.wo-status-draft {
      background: #e9ecef;
      color: #495057;
    }

    &.wo-status-planning {
      background: #dbeafe;
      color: #1e40af;
    }

    &.wo-status-awaiting {
      background: #ede9fe;
      color: #5b21b6;
    }

    &.wo-status-in-production {
      background: #d1fae5;
      color: #065f46;
    }

    &.wo-status-review {
      background: #fef3c7;
      color: #92400e;
    }

    &.wo-status-complete {
      background: #d1fae5;
      color: #065f46;
    }

    &.wo-status-cancelled {
      background: #fee2e2;
      color: #991b1b;
    }
  }

  // ── Grid footer ────────────────────────────────────────────────────────────
  .cfg-wo-footer {
    padding: 10px 18px;
    font-size: 12px;
    color: #6c757d;
    border-top: 1px solid #f2f4f7;
  }
}
</style>
